import { setup, assign, fromPromise, raise } from "xstate"
import moment from "moment"
import { MESSAGES_DDA, PCE_DDA_STATUS } from "@core/constants"
import type { Services } from "@services"
import { inject } from "vue"
import type { Source } from "@types"

// ========================== //
// Types                      //
// ========================== //

export interface GrdfDDAContext {
    /**
     * Identifiant du compteur
     */
    _id_source?: string
    /**
     * Identifiant du consentement
     */
    _id_consent?: string
    /**
     * Code postal du compteur
     */
    zipcode?: string
    /**
     * Etat du droit d'accès GRDF
     */
    status?: string
    /**
     * Raison du passage
     */
    reason?: string | null
    /**
     * Code erreur
     */
    code_error?: string | null
    /**
     * Date du dernier consentement (format ISO 8601) - sert à vérifier la validité du consentement
     */
    date_last_consent?: string | null
    /**
     * Date de début d'autorisation de récupération des données GRDF
     */
    date_from_data?: string | null
    /**
     * Dernière source récupérée par la machine (pour que le composant l'émette au parent qui met à jour le store)
     */
    _lastFetchedSource?: Source | null
    /**
     * Vrai dès qu'un "J'ai validé" / mise à jour live a été tenté — sert à afficher un retour
     * explicite quand la DDA est toujours "À valider" après le re-check (pas encore validée côté GRDF).
     */
    update_checked?: boolean
}

export type GrdfDDAEvent =
    | { type: "RELOAD_CONTEXT"; data: Partial<GrdfDDAContext> }
    | { type: "REDIRECTION" }
    | { type: "START" }
    | { type: "UPDATE_DATE_FROM_DATA"; date_from_data: string }
    | { type: "UPDATE_ZIPCODE"; zipcode: string }
    | { type: "CLEAR_LAST_FETCHED" }
    /** Retour au formulaire date / code postal (parcours Erreur après passage dans create_dda_workflow). */
    | { type: "BACK_TO_EDIT_FIELD" }
    /** Rafraîchit l'état réel auprès de GRDF depuis n'importe quel état (notamment terminaux). */
    | { type: "REFRESH" }

// ========================== //
// Guards                     //
// ========================== //
const guards = {
    didConsentValid: ({ context }: { context: GrdfDDAContext }) => {
        return moment().diff(moment(context.date_last_consent)) < 0
    },
    didConsentExpired: ({ context }: { context: GrdfDDAContext }) => {
        return moment().diff(moment(context.date_last_consent)) >= 0
    },
    didConsentValidButNoDateFromData: ({
        context,
    }: {
        context: GrdfDDAContext
    }) => {
        return (
            moment().diff(moment(context.date_last_consent)) < 0 &&
            !context.date_from_data
        )
    },
    // Génération des fonctions de conditions pour vérifier le bon état du droit d'accès. (isRightAccessState...)
    isRightAccessStateActive: ({ context }: { context: GrdfDDAContext }) =>
        context.status === PCE_DDA_STATUS.ACTIVE,
    isRightAccessStateRevoked: ({ context }: { context: GrdfDDAContext }) =>
        context.status === PCE_DDA_STATUS.REVOKED,
    isRightAccessStateRefused: ({ context }: { context: GrdfDDAContext }) =>
        context.status === PCE_DDA_STATUS.REFUSED,
    isRightAccessStateA_Valider: ({ context }: { context: GrdfDDAContext }) =>
        context.status === PCE_DDA_STATUS.A_VALIDER,
    isRightAccessStateErreur: ({ context }: { context: GrdfDDAContext }) =>
        context.status === PCE_DDA_STATUS.ERROR,
    isRightAccessStateObsolete: ({ context }: { context: GrdfDDAContext }) =>
        context.status === PCE_DDA_STATUS.OBSOLETE,
    isRightAccessStateEmpty: ({ context }: { context: GrdfDDAContext }) =>
        context.status === PCE_DDA_STATUS.EMPTY,
    isRightAccessStateRecheck: ({ context }: { context: GrdfDDAContext }) =>
        context.status === PCE_DDA_STATUS.RECHECK,
}

/**
 * Création d'un sous état pour gérer un appel API
 * @param actor - L'acteur (fonction) appelant l'API
 * @param input - Les inputs de l'acteur
 * @param successTarget - Etat cible en cas de succès
 * @param directly - Indique si l'appel doit être fait directement ou après un événement START
 * @param extraStates - Etats supplémentaires à gérer
 * @param onSuccessActions - Actions à exécuter sur succès (ex. assigner le résultat au contexte)
 * @returns
 */
function createAPISubState({
    actor = "",
    input = {},
    successTarget = "",
    directly = false,
    extraStates = {},
    onSuccessActions,
}: {
    actor: string
    input: any
    successTarget?: string
    directly?: boolean
    extraStates?: Record<string, any>
    onSuccessActions?: string[]
}) {
    return {
        initial: "idle" as const,
        states: {
            idle: directly
                ? {
                      always: {
                          target: "loading",
                      },
                  }
                : {
                      on: {
                          START: "loading",
                          ...extraStates,
                      },
                  },
            loading: {
                invoke: {
                    id: "api_call",
                    src: actor,
                    input,
                    onDone: onSuccessActions
                        ? {
                              target: "success",
                              actions: onSuccessActions,
                          }
                        : "success",
                    onError: "failure",
                },
            },
            success: successTarget
                ? {
                      always: {
                          target: successTarget,
                      },
                  }
                : {},
            failure: {
                on: {
                    START: "loading",
                    ...extraStates,
                },
            },
        },
    } as any
}

/**
 * Permet de créer le sous état pour gérer le bon message de support
 * @param isGrdf - Indique si le support est pour GRDF ou non
 * @returns Le sous état de support
 */
function createSupportState(isGrdf = false) {
    return {
        initial: isGrdf ? ("support_grdf" as const) : ("support" as const),
        states: {
            support: {
                type: "final" as const,
            },
            support_grdf: {
                type: "final" as const,
            },
        },
    } as any
}

// ========================== //
// Main Machine               //
// ========================== //

export const useGrdfMachine = () => {
    // ========================== //
    // Actions                    //
    // ========================== //

    /**
     * Injection des services
     */
    const services = inject<Services>("services")

    // ========================== //
    // Sub-states                 //
    // ========================== //

    /**
     * Création du sous état pour crée une nouvelle déclaration
     * @param stateFrom - Etat parent appelant la création de la déclaration
     * @param editDateFromData - Indique si la date de début doit être modifiée
     * @returns
     */
    function createDDAWorkflow(
        stateFrom: string,
        backToUpdateFieldTarget?: string
    ) {
        return {
            initial: "idle",
            ...(backToUpdateFieldTarget
                ? {
                      on: {
                          BACK_TO_EDIT_FIELD: {
                              target: backToUpdateFieldTarget,
                          },
                      },
                  }
                : {}),
            states: {
                idle: {
                    always: [
                        {
                            target: "update_consent",
                            guard: "didConsentValidButNoDateFromData",
                        },
                        {
                            target: "create_dda",
                            guard: "didConsentValid",
                        },
                        {
                            target: "consent_expired",
                            guard: "didConsentExpired",
                        },
                    ],
                },
                update_consent: createAPISubState({
                    actor: "fetchUpdateDateFromData",
                    input: ({ context }: { context: GrdfDDAContext }) => ({
                        date_from_data: context.date_from_data!,
                        _id_consent: context._id_consent!,
                    }),
                    // stateFrom est déjà un id complet `#grdf-dda.…` — ne pas re-préfixer
                    // (l'ancien `#grdf-dda.${stateFrom}` produisait un id invalide, transition morte).
                    successTarget: `${stateFrom}.create_dda`,
                    // Permet la saisie de la date dans le formulaire tant qu'on est dans ce sous-état
                    // (sinon le contexte ne se met jamais à jour et le submit reste désactivé).
                    extraStates: {
                        UPDATE_DATE_FROM_DATA: {
                            actions: "updateDateFromData",
                        },
                    },
                }),
                create_dda: {
                    initial: "idle",
                    states: {
                        idle: {
                            on: {
                                START: "loading",
                            },
                        },
                        loading: {
                            invoke: {
                                src: "fetchDeclare",
                                input: ({
                                    context,
                                }: {
                                    context: GrdfDDAContext
                                }) => ({
                                    _id_consent: context._id_consent!,
                                }),
                                onDone: "reload_source",
                                onError: "reload_source",
                            },
                        },
                        failure: {
                            on: {
                                START: "loading",
                            },
                        },
                        reload_source: createAPISubState({
                            actor: "fetchSource",
                            input: ({
                                context,
                            }: {
                                context: GrdfDDAContext
                            }) => ({
                                _id_source: context._id_source!,
                            }),
                            successTarget: "#grdf-dda.idle",
                            directly: true,
                            onSuccessActions: ["assignFetchedSource"],
                        }),
                    },
                },
                consent_expired: {
                    type: "final",
                },
            },
        } as any
    }

    const machine = setup({
        types: {
            input: {} as Partial<GrdfDDAContext>,
            context: {} as GrdfDDAContext,
            events: {} as GrdfDDAEvent,
        },
        guards,
        actions: {
            /**
             * Met à jour la date de début d'autorisation de récupération des données GRDF
             */
            updateDateFromData: assign({
                date_from_data: ({ event }: { event: GrdfDDAEvent }) => {
                    return event.type === "UPDATE_DATE_FROM_DATA"
                        ? event.date_from_data
                        : undefined
                },
            }),
            /**
             * Met à jour le code postal du compteur
             */
            updateZipcode: assign({
                zipcode: ({ event }: { event: GrdfDDAEvent }) => {
                    return event.type === "UPDATE_ZIPCODE"
                        ? event.zipcode
                        : undefined
                },
            }),
            /**
             * Recharge le contexte de la machine avec les nouvelles données
             */
            reloadContext: assign({
                _id_source: ({ event }: { event: GrdfDDAEvent }) =>
                    event.type === "RELOAD_CONTEXT"
                        ? event.data?._id_source
                        : undefined,
                _id_consent: ({ event }: { event: GrdfDDAEvent }) =>
                    event.type === "RELOAD_CONTEXT"
                        ? event.data?._id_consent
                        : undefined,
                zipcode: ({ event }: { event: GrdfDDAEvent }) =>
                    event.type === "RELOAD_CONTEXT"
                        ? event.data?.zipcode
                        : undefined,
                status: ({ event }: { event: GrdfDDAEvent }) =>
                    event.type === "RELOAD_CONTEXT"
                        ? event.data?.status
                        : undefined,
                reason: ({ event }: { event: GrdfDDAEvent }) =>
                    event.type === "RELOAD_CONTEXT"
                        ? event.data?.reason
                        : undefined,
                code_error: ({ event }: { event: GrdfDDAEvent }) =>
                    event.type === "RELOAD_CONTEXT"
                        ? event.data?.code_error
                        : undefined,
                date_last_consent: ({ event }: { event: GrdfDDAEvent }) =>
                    event.type === "RELOAD_CONTEXT"
                        ? event.data?.date_last_consent
                        : undefined,
                date_from_data: ({ event }: { event: GrdfDDAEvent }) =>
                    event.type === "RELOAD_CONTEXT"
                        ? event.data?.date_from_data
                        : undefined,
            }),
            /**
             * Stocke la source venant du fetch pour que le composant puisse l'émettre au parent (mise à jour du store).
             */
            assignFetchedSource: assign({
                _lastFetchedSource: ({
                    event,
                }: {
                    event: { output?: Source }
                }) => event?.output ?? null,
            }),
            /**
             * Réinitialise _lastFetchedSource après émission au parent.
             */
            clearLastFetchedSource: assign({
                _lastFetchedSource: undefined,
            }),
            /**
             * Marque qu'un re-check "J'ai validé" a été tenté (pour le retour "toujours en attente").
             */
            markUpdateChecked: assign({
                update_checked: true,
            }),
        },
        actors: {
            fetchDeclare: fromPromise(
                async ({ input }: { input: { _id_consent: string } }) => {
                    return services?.$declare(input._id_consent)
                }
            ),
            fetchLastDeclaration: fromPromise(
                async ({ input }: { input: { _id_source: string } }) => {
                    return services?.$getLastDeclarationByIdSource(
                        input._id_source
                    )
                }
            ),
            fetchSource: fromPromise(
                async ({ input }: { input: { _id_source: string } }) => {
                    if (!services?.$getSourcesInformations) {
                        throw new Error(
                            "Service $getSourcesInformations non disponible"
                        )
                    }
                    if (!services?.$getSourceByIdOldVersion) {
                        throw new Error(
                            "Service $getSourceByIdOldVersion non disponible"
                        )
                    }
                    const [sources, sourceLegacy] = await Promise.all([
                        services.$getSourcesInformations([input._id_source]),
                        services.$getSourceByIdOldVersion(input._id_source),
                    ])
                    const sourceDapi = Array.isArray(sources)
                        ? sources[0]
                        : (sources ?? {})
                    return {
                        ...sourceDapi,
                        ...sourceLegacy,
                    } as Source
                }
            ),
            fetchUpdateZipcode: fromPromise(
                async ({
                    input,
                }: {
                    input: { zipcode: string; _id_consent: string }
                }) => {
                    return services?.$updateZipcodeByConsentId(
                        input._id_consent,
                        input.zipcode
                    )
                }
            ),
            fetchUpdateDateFromData: fromPromise(
                async ({
                    input,
                }: {
                    input: { date_from_data: string; _id_consent: string }
                }) => {
                    return services?.$updateDateFromDataByConsentId(
                        input._id_consent,
                        input.date_from_data
                    )
                }
            ),
        },
    }).createMachine({
        id: "grdf-dda",
        initial: "idle",
        context: ({ input }) => ({
            _id_source: input?._id_source || "",
            _id_consent: input?._id_consent || "",
            zipcode: input?.zipcode || "",
            status: input?.status || PCE_DDA_STATUS.REFUSED,
            reason: input?.reason || null,
            code_error: input?.code_error || null,
            date_last_consent: input?.date_last_consent || null,
            date_from_data: input?.date_from_data || null,
        }),
        on: {
            /**
             * Recharge le contexte puis repasse par l'état racine `idle` pour réévaluer les gardes REDIRECTION
             * (sinon la machine reste dans une branche obsolète quand les props changent, ex. stories Storybook).
             */
            RELOAD_CONTEXT: {
                target: "#grdf-dda.idle",
                reenter: true,
                actions: "reloadContext",
            },
            CLEAR_LAST_FETCHED: {
                actions: "clearLastFetchedSource",
            },
            /**
             * Re-consulte GRDF en live depuis n'importe quel état (notamment les états
             * terminaux Obsolète/Révoqué/Erreur) puis recharge la source et réévalue.
             * Permet à l'utilisateur de rafraîchir l'état réel côté GRDF (et de corriger
             * une éventuelle mauvaise prise en compte côté Homeys).
             */
            REFRESH: {
                target: "#grdf-dda.refreshing",
            },
        },
        states: {
            idle: {
                entry: raise({ type: "REDIRECTION" }),
                on: {
                    REDIRECTION: [
                        {
                            target: PCE_DDA_STATUS.ACTIVE,
                            guard: "isRightAccessStateActive",
                        },
                        {
                            target: [PCE_DDA_STATUS.REVOKED],
                            guard: "isRightAccessStateRevoked",
                        },
                        {
                            target: [PCE_DDA_STATUS.EMPTY],
                            guard: "isRightAccessStateEmpty",
                        },
                        {
                            target: PCE_DDA_STATUS.A_VALIDER,
                            guard: "isRightAccessStateA_Valider",
                        },
                        {
                            target: [PCE_DDA_STATUS.REFUSED],
                            guard: "isRightAccessStateRefused",
                        },
                        {
                            target: [PCE_DDA_STATUS.OBSOLETE],
                            guard: "isRightAccessStateObsolete",
                        },
                        {
                            target: PCE_DDA_STATUS.ERROR,
                            guard: "isRightAccessStateErreur",
                        },
                        {
                            target: [PCE_DDA_STATUS.RECHECK],
                            guard: "isRightAccessStateRecheck",
                        },
                    ],
                },
            },
            /**
             * Rafraîchissement live depuis GRDF (déclenché par REFRESH) : re-consulte
             * la dernière déclaration, recharge la source, puis retombe sur idle pour
             * réévaluer l'état réel. Réutilisable depuis tout état (cf. handler REFRESH global).
             */
            refreshing: {
                initial: "loading",
                states: {
                    loading: {
                        invoke: {
                            id: "refresh_get_last_declaration",
                            src: "fetchLastDeclaration",
                            input: ({
                                context,
                            }: {
                                context: GrdfDDAContext
                            }) => ({
                                _id_source: context._id_source!,
                            }),
                            onDone: "reload_source",
                            onError: "failure",
                        },
                    },
                    failure: {
                        on: {
                            REFRESH: "loading",
                        },
                    },
                    reload_source: createAPISubState({
                        actor: "fetchSource",
                        input: ({ context }: { context: GrdfDDAContext }) => ({
                            _id_source: context._id_source!,
                        }),
                        successTarget: "#grdf-dda.idle",
                        directly: true,
                        onSuccessActions: ["assignFetchedSource"],
                    }),
                },
            },
            [PCE_DDA_STATUS.ACTIVE]: {
                type: "final",
            },
            [PCE_DDA_STATUS.REVOKED]: createSupportState(false),
            [PCE_DDA_STATUS.EMPTY]: createDDAWorkflow(
                `#grdf-dda.${PCE_DDA_STATUS.EMPTY}`
            ),
            [PCE_DDA_STATUS.A_VALIDER]: {
                initial: "idle",
                states: {
                    idle: {
                        on: {
                            START: {
                                target: "loading",
                                actions: "markUpdateChecked",
                            },
                        },
                    },
                    loading: {
                        invoke: {
                            id: "get_last_declaration",
                            src: "fetchLastDeclaration",
                            input: ({ context }) => ({
                                _id_source: context._id_source!,
                            }),
                            onDone: "reload_source",
                            onError: "failure",
                        },
                    },
                    failure: {
                        on: {
                            START: {
                                target: "loading",
                                actions: "markUpdateChecked",
                            },
                        },
                    },
                    reload_source: createAPISubState({
                        actor: "fetchSource",
                        input: ({ context }: { context: GrdfDDAContext }) => ({
                            _id_source: context._id_source!,
                        }),
                        successTarget: "#grdf-dda.idle",
                        directly: true,
                        onSuccessActions: ["assignFetchedSource"],
                    }),
                },
            },
            [PCE_DDA_STATUS.REFUSED]: {
                initial: "idle",
                states: {
                    idle: {
                        always: [
                            {
                                target: "case_1",
                                guard: ({ context }) =>
                                    context.reason ===
                                    MESSAGES_DDA.REFUSED.CASE_1,
                            },
                            {
                                target: "case_2",
                                guard: ({ context }) =>
                                    context.reason ===
                                    MESSAGES_DDA.REFUSED.CASE_2,
                            },
                            {
                                target: "case_3",
                                guard: ({ context }) =>
                                    context.reason ===
                                    MESSAGES_DDA.REFUSED.CASE_3,
                            },
                            {
                                target: "case_4",
                                guard: ({ context }) =>
                                    context.reason ===
                                    MESSAGES_DDA.REFUSED.CASE_4,
                            },
                        ],
                    },
                    case_1: createDDAWorkflow(
                        `#grdf-dda.${PCE_DDA_STATUS.REFUSED}.case_1`
                    ),
                    // DEV-674 : refus titulaire — une nouvelle demande est autorisée par GRDF,
                    // on monte le workflow de redéclaration (plus de cul-de-sac support).
                    case_2: createDDAWorkflow(
                        `#grdf-dda.${PCE_DDA_STATUS.REFUSED}.case_2`
                    ),
                    case_3: createDDAWorkflow(
                        `#grdf-dda.${PCE_DDA_STATUS.REFUSED}.case_3`
                    ),
                    // DEV-674 : changement de titulaire — redéclarable si le consentement a été
                    // refait au nom du nouveau titulaire (cas GI140515).
                    case_4: createDDAWorkflow(
                        `#grdf-dda.${PCE_DDA_STATUS.REFUSED}.case_4`
                    ),
                },
            },
            [PCE_DDA_STATUS.OBSOLETE]: {
                initial: "idle",
                states: {
                    idle: {
                        always: [
                            {
                                target: "case_1",
                                guard: ({ context }) =>
                                    context.reason ===
                                    MESSAGES_DDA.OBSOLETE.CASE_1,
                            },
                            {
                                target: "case_2",
                                guard: ({ context }) =>
                                    context.reason ===
                                    MESSAGES_DDA.OBSOLETE.CASE_2,
                            },
                            {
                                target: "case_3",
                                guard: ({ context }) =>
                                    context.reason ===
                                    MESSAGES_DDA.OBSOLETE.CASE_3,
                            },
                            {
                                target: "case_4",
                                guard: ({ context }) =>
                                    context.reason ===
                                    MESSAGES_DDA.OBSOLETE.CASE_4,
                            },
                            {
                                target: "case_5",
                                guard: ({ context }) =>
                                    context.reason ===
                                    MESSAGES_DDA.OBSOLETE.CASE_5,
                            },
                        ],
                    },
                    // workflow déclarer nouvelle DDA
                    case_1: createDDAWorkflow(
                        `#grdf-dda.${PCE_DDA_STATUS.OBSOLETE}.case_1`
                    ),
                    // workflow déclarer nouvelle DDA
                    case_2: createDDAWorkflow(
                        `#grdf-dda.${PCE_DDA_STATUS.OBSOLETE}.case_2`
                    ),
                    // DEV-674 : états précédemment terminaux (support) — on monte le workflow de
                    // redéclaration pour ne plus bloquer l'utilisateur quand un consentement signé
                    // valide existe. Les boutons « Vérifier l'état » + « Support GRDF » restent
                    // affichés via le composant (conditions sur status/reason).
                    // case_3 : DDA passé Obsolète par GRDF (outils internes).
                    case_3: createDDAWorkflow(
                        `#grdf-dda.${PCE_DDA_STATUS.OBSOLETE}.case_3`
                    ),
                    // case_4 : changement de titulaire — redéclarable si le consentement a été
                    // refait au nom du nouveau titulaire (cas GI140515, DEV-674).
                    case_4: createDDAWorkflow(
                        `#grdf-dda.${PCE_DDA_STATUS.OBSOLETE}.case_4`
                    ),
                    // case_5 : email titulaire invalide — redéclarable une fois l'adresse corrigée
                    // (nouveau consentement / changement de titulaire).
                    case_5: createDDAWorkflow(
                        `#grdf-dda.${PCE_DDA_STATUS.OBSOLETE}.case_5`
                    ),
                },
            },
            [PCE_DDA_STATUS.ERROR]: {
                initial: "idle",
                states: {
                    idle: {
                        always: [
                            {
                                target: "error_date_from_data",
                                guard: ({ context }) =>
                                    [
                                        MESSAGES_DDA.ERROR.CASE_1,
                                        MESSAGES_DDA.ERROR.CASE_7,
                                    ].includes(context.code_error || ""),
                            },
                            {
                                target: "error_unknown_pce",
                                guard: ({ context }) =>
                                    context.code_error ===
                                    MESSAGES_DDA.ERROR.CASE_2,
                            },
                            {
                                target: "error_zipcode",
                                guard: ({ context }) =>
                                    [
                                        MESSAGES_DDA.ERROR.CASE_3,
                                        MESSAGES_DDA.ERROR.CASE_4,
                                        MESSAGES_DDA.ERROR.CASE_5,
                                    ].includes(context.code_error || ""),
                            },
                            {
                                target: "error_contrat_echu",
                                guard: ({ context }) =>
                                    context.code_error ===
                                    MESSAGES_DDA.ERROR.CASE_6,
                            },
                            {
                                target: "unknown_error",
                            },
                        ],
                    },
                    // Il manque le champ "date_from_data" dans le consentement pour déclarer la DDA
                    error_date_from_data: {
                        initial: "update_field",
                        states: {
                            update_field: {
                                ...createAPISubState({
                                    actor: "fetchUpdateDateFromData",
                                    input: ({
                                        context,
                                    }: {
                                        context: GrdfDDAContext
                                    }) => ({
                                        date_from_data: context.date_from_data!,
                                        _id_consent: context._id_consent!,
                                    }),
                                    successTarget: `#grdf-dda.${PCE_DDA_STATUS.ERROR}.error_date_from_data.create_dda_workflow`,
                                    extraStates: {
                                        UPDATE_DATE_FROM_DATA: {
                                            actions: "updateDateFromData",
                                        },
                                    },
                                }),
                            },
                            create_dda_workflow: createDDAWorkflow(
                                `#grdf-dda.${PCE_DDA_STATUS.ERROR}.error_date_from_data.create_dda_workflow`,
                                `#grdf-dda.${PCE_DDA_STATUS.ERROR}.error_date_from_data.update_field`
                            ),
                        },
                    },

                    // Le code postal associé au PCE est inconnu.
                    error_zipcode: {
                        initial: "update_field",
                        states: {
                            update_field: {
                                ...createAPISubState({
                                    actor: "fetchUpdateZipcode",
                                    input: ({
                                        context,
                                    }: {
                                        context: GrdfDDAContext
                                    }) => ({
                                        zipcode: context.zipcode!,
                                        _id_consent: context._id_consent!,
                                    }),
                                    successTarget: `#grdf-dda.${PCE_DDA_STATUS.ERROR}.error_zipcode.create_dda_workflow`,
                                    extraStates: {
                                        UPDATE_ZIPCODE: {
                                            actions: "updateZipcode",
                                        },
                                    },
                                }),
                            },
                            create_dda_workflow: createDDAWorkflow(
                                `#grdf-dda.${PCE_DDA_STATUS.ERROR}.error_zipcode.create_dda_workflow`,
                                `#grdf-dda.${PCE_DDA_STATUS.ERROR}.error_zipcode.update_field`
                            ),
                        },
                    },
                    // Le numéro de PCE est inconnu
                    error_unknown_pce: createSupportState(true),
                    // Le contrat de fourniture de gaz est échu.
                    error_contrat_echu: createSupportState(true),
                    // Autre erreur non gérée
                    unknown_error: createSupportState(true),
                },
            },
            [PCE_DDA_STATUS.RECHECK]: createDDAWorkflow(
                `#grdf-dda.${PCE_DDA_STATUS.RECHECK}`
            ),
        },
    })

    return {
        machine,
    }
}

export type GrdfDDAMachine = ReturnType<typeof useGrdfMachine>["machine"]
